import React, { useMemo, useState } from "react";

// Default rows matching the xlsx ROE sheet exactly
const DEFAULT_ROE_ROWS = [
  {
    id: "roe-001",
    item: "FBDA",
    tenderedForeignPrice: 269854.28,
    currency: "EUR",
    tenderRoe: 20,
    confirmedForwardCoverRoe: 21.95,
    unit: "sum",
    quantity: 1,
    actualClaimedFC: 126520.05, // Column V: actual FC amount claimed (drives % Complete)
    percentPrevious: 0,
    certificateNo: "",
    claimMonth: "",
    status: "Draft",
    receivedFrom: "",
    submittedTo: "",
  },
  {
    id: "roe-002",
    item: "Blowers",
    tenderedForeignPrice: 265000,
    currency: "GBP",
    tenderRoe: 22.72,
    confirmedForwardCoverRoe: 24.408,
    unit: "sum",
    quantity: 1,
    actualClaimedFC: 0,
    percentPrevious: 0,
    certificateNo: "",
    claimMonth: "",
    status: "Draft",
    receivedFrom: "",
    submittedTo: "",
  },
  {
    id: "roe-003",
    item: "Actuators",
    tenderedForeignPrice: 33154.56,
    currency: "GBP",
    tenderRoe: 21.77,
    confirmedForwardCoverRoe: 0,
    unit: "sum",
    quantity: 1,
    actualClaimedFC: 0,
    percentPrevious: 0,
    certificateNo: "",
    claimMonth: "",
    status: "Draft",
    receivedFrom: "",
    submittedTo: "",
  },
  {
    id: "roe-004",
    item: "Instruments",
    tenderedForeignPrice: 92826.32,
    currency: "EUR",
    tenderRoe: 20.12,
    confirmedForwardCoverRoe: 0,
    unit: "sum",
    quantity: 1,
    actualClaimedFC: 0,
    percentPrevious: 0,
    certificateNo: "",
    claimMonth: "",
    status: "Draft",
    receivedFrom: "",
    submittedTo: "",
  },
  {
    id: "roe-005",
    item: "ABB MV Switchgear",
    tenderedForeignPrice: 71623.76,
    currency: "EUR",
    tenderRoe: 19.84,
    confirmedForwardCoverRoe: 0,
    unit: "sum",
    quantity: 1,
    actualClaimedFC: 0,
    percentPrevious: 0,
    certificateNo: "",
    claimMonth: "",
    status: "Draft",
    receivedFrom: "",
    submittedTo: "",
  },
  {
    id: "roe-006",
    item: "ABB MV Switchgear",
    tenderedForeignPrice: 54090.17,
    currency: "USD",
    tenderRoe: 18.12,
    confirmedForwardCoverRoe: 0,
    unit: "sum",
    quantity: 1,
    actualClaimedFC: 0,
    percentPrevious: 0,
    certificateNo: "",
    claimMonth: "",
    status: "Draft",
    receivedFrom: "",
    submittedTo: "",
  },
  {
    id: "roe-007",
    item: "DPFC",
    tenderedForeignPrice: 47097.22,
    currency: "USD",
    tenderRoe: 18.2,
    confirmedForwardCoverRoe: 0,
    unit: "sum",
    quantity: 1,
    actualClaimedFC: 0,
    percentPrevious: 0,
    certificateNo: "",
    claimMonth: "",
    status: "Draft",
    receivedFrom: "",
    submittedTo: "",
  },
  {
    id: "roe-008",
    item: "Generator",
    tenderedForeignPrice: 496000,
    currency: "USD",
    tenderRoe: 19.15,
    confirmedForwardCoverRoe: 0,
    unit: "sum",
    quantity: 1,
    actualClaimedFC: 0,
    percentPrevious: 0,
    certificateNo: "",
    claimMonth: "",
    status: "Draft",
    receivedFrom: "",
    submittedTo: "",
  },
];

const STATUS_OPTIONS = ["Draft", "Submitted", "Under Assessment", "Approved", "Rejected", "Included in PC", "Closed"];
const CURRENCY_OPTIONS = ["EUR", "GBP", "USD", "ZAR"];

const n = (v) => { const p = Number(v); return Number.isFinite(p) ? p : 0; };
const clamp = (v) => Math.min(100, Math.max(0, n(v)));

const fmtZAR = (v) =>
  new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n(v));

const fmtNum = (v, d = 4) =>
  new Intl.NumberFormat("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n(v));

// ─── Core calculation matching xlsx formulas exactly ───────────────────────
// E = B*D  (tenderedRandValue = tenderedForeignPrice * tenderRoe)
// G = F-D  (varianceInRoe = confirmedForwardCoverRoe - tenderRoe)
// H = E/D*F - E  (valueOfVariance = tenderedRandValue / tenderRoe * confirmedRoe - tenderedRandValue)
//   simplified: = tenderedForeignPrice * (confirmedRoe - tenderRoe)
// I = E/D*F  (confirmedRandValue = tenderedForeignPrice * confirmedRoe)
// M = H  (rate = valueOfVariance)
// N = L*M  (total = quantity * rate)
// O = V/B  (percentComplete = actualClaimedFC / tenderedForeignPrice)  [as decimal]
// Q = N*O  (totalClaimed)
// R = N*P  (previousClaim, P as decimal)
// S = Q-R  (thisClaim)
// T = N-Q  (balance)
export const calculateRoeRow = (row) => {
  const B = n(row.tenderedForeignPrice);
  const D = n(row.tenderRoe);
  const F = n(row.confirmedForwardCoverRoe);
  const L = n(row.quantity) || 1;
  const V = n(row.actualClaimedFC);
  const P = clamp(row.percentPrevious) / 100;

  const E = B * D;                          // Tendered price in Rands
  const G = F - D;                          // Variance in ROE
  const H = B * G;                          // Value of variance  (= E/D*F - E simplified)
  const I = B * F;                          // Value in Rands based on confirmed ROE
  const M = H;                              // Rate
  const N = L * M;                          // Total
  const O = B !== 0 ? V / B : 0;           // % Complete (decimal)
  const Q = N * O;                          // Total Claimed
  const R = N * P;                          // Previous Claim
  const S = Q - R;                          // This Claim
  const T = N - Q;                          // Balance

  return {
    ...row,
    tenderedRandValue: E,
    varianceInRoe: G,
    valueOfVariance: H,
    confirmedRandValue: I,
    rate: M,
    total: N,
    percentComplete: O * 100,              // store as % for display
    totalClaimed: Q,
    previousClaim: R,
    thisClaim: S,
    balance: T,
  };
};

export const buildRoeRowsWithCalculations = (rows = []) => {
  const calculated = rows.map(calculateRoeRow);
  // Attach a running cumulative ROE total (carries forward across all rows in order)
  let running = 0;
  return calculated.map(row => {
    running += n(row.thisClaim);
    return { ...row, cumulativeRoe: running };
  });
};

export const getRoeThisClaimForCertificate = (rows = [], certificateNo) => {
  const cert = String(certificateNo || "").trim();
  if (!cert) return 0;
  return buildRoeRowsWithCalculations(rows).reduce(
    (sum, row) => (String(row.certificateNo || "").trim() === cert ? sum + n(row.thisClaim) : sum), 0
  );
};

export const getRoeThisClaimForMonth = (rows = [], claimMonth) => {
  const month = String(claimMonth || "").trim();
  if (!month) return 0;
  return buildRoeRowsWithCalculations(rows).reduce(
    (sum, row) => (String(row.claimMonth || "").trim() === month ? sum + n(row.thisClaim) : sum), 0
  );
};

/**
 * Returns the CUMULATIVE ROE up to and including the given certificate.
 * orderedCertNos is an array of certificate numbers in chronological order.
 * All certs up to and including certNo are summed.
 */
export const getRoeCumulativeUpToCertificate = (rows = [], orderedCertNos = [], certNo) => {
  const target = String(certNo || "").trim();
  if (!target) return 0;
  const idx = orderedCertNos.findIndex(c => String(c || "").trim() === target);
  if (idx === -1) return 0;
  const certsUpToAndIncluding = new Set(orderedCertNos.slice(0, idx + 1).map(c => String(c || "").trim()));
  return buildRoeRowsWithCalculations(rows).reduce(
    (sum, row) => (certsUpToAndIncluding.has(String(row.certificateNo || "").trim()) ? sum + n(row.thisClaim) : sum), 0
  );
};

// ─── Component ─────────────────────────────────────────────────────────────
export default function ROEsection({
  roeRows,
  setRoeRows,
  certificateOptions = [],
  selectedCertificateNo = "",
  selectedClaimMonth = "",
}) {
  const [localRows, setLocalRows] = useState(DEFAULT_ROE_ROWS);
  const rows = Array.isArray(roeRows) ? roeRows : localRows;
  const updateRows = typeof setRoeRows === "function" ? setRoeRows : setLocalRows;

  const calculatedRows = useMemo(() => buildRoeRowsWithCalculations(rows), [rows]);

  const totals = useMemo(() =>
    calculatedRows.reduce((acc, row) => {
      acc.tenderedRandValue   += n(row.tenderedRandValue);
      acc.valueOfVariance     += n(row.valueOfVariance);
      acc.confirmedRandValue  += n(row.confirmedRandValue);
      acc.total               += n(row.total);
      acc.totalClaimed        += n(row.totalClaimed);
      acc.previousClaim       += n(row.previousClaim);
      acc.thisClaim           += n(row.thisClaim);
      acc.balance             += n(row.balance);
      return acc;
    }, { tenderedRandValue: 0, valueOfVariance: 0, confirmedRandValue: 0, total: 0, totalClaimed: 0, previousClaim: 0, thisClaim: 0, balance: 0 }),
    [calculatedRows]
  );

  const selectedCertificateTotal = useMemo(() => getRoeThisClaimForCertificate(rows, selectedCertificateNo), [rows, selectedCertificateNo]);
  const selectedMonthTotal       = useMemo(() => getRoeThisClaimForMonth(rows, selectedClaimMonth),         [rows, selectedClaimMonth]);

  const updateRow = (id, field, value) =>
    updateRows((prev) => prev.map((r) => (r.id !== id ? r : { ...r, [field]: value })));

  const addRow = () =>
    updateRows((prev) => [
      ...prev,
      {
        id: `roe-${Date.now()}`,
        item: "",
        tenderedForeignPrice: 0,
        currency: "EUR",
        tenderRoe: 0,
        confirmedForwardCoverRoe: 0,
        unit: "sum",
        quantity: 1,
        actualClaimedFC: 0,
        percentPrevious: 0,
        certificateNo: certificateOptions[0] || "",
        claimMonth: "",
        status: "Draft",
        receivedFrom: "",
        submittedTo: "",
      },
    ]);

  const deleteRow = (id) => updateRows((prev) => prev.filter((r) => r.id !== id));

  return (
    <div style={{ display: "grid", gap: 16, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#0f172a" }}>Rate of Exchange (ROE)</h2>
          <p style={{ margin: "5px 0 0", color: "#64748b", fontSize: 13 }}>
            Foreign currency ROE claims — % Complete driven by actual FC amount claimed (col V in xlsx).
          </p>
        </div>
        <button
          type="button"
          onClick={addRow}
          style={{
            padding: "9px 16px", borderRadius: 8, border: "1px solid #0f172a",
            background: "#0f172a", color: "white", cursor: "pointer", fontWeight: 600, fontSize: 13,
          }}
        >
          + Add ROE Item
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))", gap: 10 }}>
        {[
          { label: "This Claim (Total)", value: totals.thisClaim },
          { label: "Total Claimed to Date", value: totals.totalClaimed },
          { label: "Previous Claim", value: totals.previousClaim },
          { label: "ROE Balance", value: totals.balance },
          { label: "Selected Certificate ROE", value: selectedCertificateTotal },
          { label: "Selected Month ROE", value: selectedMonthTotal },
        ].map(({ label, value }) => (
          <div key={label} style={{ padding: "12px 14px", border: "1px solid #e2e8f0", borderRadius: 10, background: "white" }}>
            <div style={{ color: "#64748b", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, marginTop: 4, color: n(value) < 0 ? "#dc2626" : "#0f172a" }}>{fmtZAR(value)}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 2100, fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f1f5f9" }}>
              {/* Group A: Identity */}
              <Th>Item</Th>
              {/* Group B: Tendered */}
              <Th title="Tendered price in Foreign Currency (col B)">Tendered FC</Th>
              <Th title="Foreign Currency (col C)">Currency</Th>
              <Th title="ROE at tender date (col D)">Tender ROE</Th>
              <Th title="Tendered price in Rands = B×D (col E)" bg="#f8fafc">Tendered Rands</Th>
              {/* Group C: Confirmed */}
              <Th title="Confirmed forward cover ROE (col F)">Confirmed ROE</Th>
              <Th title="Variance in ROE = F−D (col G)" bg="#f8fafc">ROE Variance</Th>
              <Th title="Value of variance = B×(F−D) (col H)" bg="#f8fafc">Value of Variance</Th>
              <Th title="Value in Rands based on confirmed ROE = B×F (col I)" bg="#f8fafc">Confirmed Rand Value</Th>
              {/* Group D: Claim */}
              <Th>Unit</Th>
              <Th title="Quantity (col L)">Qty</Th>
              <Th title="Rate = Value of Variance (col M)" bg="#f8fafc">Rate</Th>
              <Th title="Total = Qty × Rate (col N)" bg="#f8fafc">Total</Th>
              {/* Group E: Actual FC */}
              <Th title="Actual FC amount claimed (col V) — drives % Complete">Actual FC Claimed</Th>
              <Th title="% Complete = Actual FC / Tendered FC (col O)" bg="#f8fafc">% Compl.</Th>
              <Th title="% Previous (col P)">% Prev.</Th>
              <Th title="Total Claimed = Total × % Compl. (col Q)" bg="#f8fafc">Total Claimed</Th>
              <Th title="Previous Claim = Total × % Prev. (col R)" bg="#f8fafc">Prev. Claim</Th>
              <Th title="This Claim = Total Claimed − Prev. Claim (col S)" bg="#e0f2fe">This Claim</Th>
              <Th title="Cumulative ROE — running total carried forward across all rows" bg="#fef9c3">Cumulative ROE</Th>
              <Th title="Balance = Total − Total Claimed (col T)" bg="#f8fafc">Balance</Th>
              {/* Admin */}
              <Th>Received From</Th>
              <Th>Submitted To</Th>
              <Th>Status</Th>
              <Th>Certificate</Th>
              <Th>Claim Month</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {calculatedRows.map((row, idx) => (
              <tr key={row.id} style={{ background: idx % 2 === 0 ? "white" : "#fafbfc" }}>
                {/* Item */}
                <Td><Input value={row.item} onChange={(v) => updateRow(row.id, "item", v)} minWidth={140} /></Td>

                {/* Tendered inputs */}
                <Td><Input type="number" value={row.tenderedForeignPrice} onChange={(v) => updateRow(row.id, "tenderedForeignPrice", v)} minWidth={120} /></Td>
                <Td>
                  <Select value={row.currency} options={CURRENCY_OPTIONS} onChange={(v) => updateRow(row.id, "currency", v)} minWidth={80} />
                </Td>
                <Td><Input type="number" value={row.tenderRoe} onChange={(v) => updateRow(row.id, "tenderRoe", v)} minWidth={90} /></Td>

                {/* Calculated: Tendered Rands */}
                <Calc>{fmtZAR(row.tenderedRandValue)}</Calc>

                {/* Confirmed ROE */}
                <Td><Input type="number" value={row.confirmedForwardCoverRoe} onChange={(v) => updateRow(row.id, "confirmedForwardCoverRoe", v)} minWidth={100} /></Td>

                {/* Calculated: Variance, Value of Variance, Confirmed Rand Value */}
                <Calc color={n(row.varianceInRoe) < 0 ? "#dc2626" : undefined}>{fmtNum(row.varianceInRoe, 4)}</Calc>
                <Calc color={n(row.valueOfVariance) < 0 ? "#dc2626" : undefined}>{fmtZAR(row.valueOfVariance)}</Calc>
                <Calc>{fmtZAR(row.confirmedRandValue)}</Calc>

                {/* Qty & Unit */}
                <Td><Input value={row.unit} onChange={(v) => updateRow(row.id, "unit", v)} minWidth={60} /></Td>
                <Td><Input type="number" value={row.quantity} onChange={(v) => updateRow(row.id, "quantity", v)} minWidth={60} /></Td>

                {/* Calculated: Rate, Total */}
                <Calc color={n(row.rate) < 0 ? "#dc2626" : undefined}>{fmtZAR(row.rate)}</Calc>
                <Calc color={n(row.total) < 0 ? "#dc2626" : undefined}>{fmtZAR(row.total)}</Calc>

                {/* Actual FC Claimed (col V) — the key input that drives % Complete */}
                <Td>
                  <Input
                    type="number"
                    value={row.actualClaimedFC}
                    onChange={(v) => updateRow(row.id, "actualClaimedFC", v)}
                    minWidth={120}
                    highlight
                  />
                </Td>

                {/* % Complete (read-only — derived from actual FC / tendered FC) */}
                <Calc>{fmtNum(row.percentComplete, 2)}%</Calc>

                {/* % Previous (editable) */}
                <Td><Input type="number" value={row.percentPrevious} onChange={(v) => updateRow(row.id, "percentPrevious", v)} minWidth={70} /></Td>

                {/* Calculated claim columns */}
                <Calc>{fmtZAR(row.totalClaimed)}</Calc>
                <Calc>{fmtZAR(row.previousClaim)}</Calc>
                <Calc strong bg="#e0f2fe" color={n(row.thisClaim) < 0 ? "#dc2626" : "#0369a1"}>{fmtZAR(row.thisClaim)}</Calc>
                <Calc strong bg="#fef9c3" color={n(row.cumulativeRoe) < 0 ? "#dc2626" : "#854d0e"}>{fmtZAR(row.cumulativeRoe)}</Calc>
                <Calc color={n(row.balance) < 0 ? "#dc2626" : undefined}>{fmtZAR(row.balance)}</Calc>

                {/* Admin */}
                <Td><Input type="date" value={row.receivedFrom} onChange={(v) => updateRow(row.id, "receivedFrom", v)} minWidth={130} /></Td>
                <Td><Input type="date" value={row.submittedTo} onChange={(v) => updateRow(row.id, "submittedTo", v)} minWidth={130} /></Td>
                <Td>
                  <Select value={row.status} options={STATUS_OPTIONS} onChange={(v) => updateRow(row.id, "status", v)} minWidth={150} />
                </Td>
                <Td>
                  <CertCell value={row.certificateNo} options={certificateOptions} onChange={(v) => updateRow(row.id, "certificateNo", v)} />
                </Td>
                <Td><Input type="month" value={row.claimMonth} onChange={(v) => updateRow(row.id, "claimMonth", v)} minWidth={130} /></Td>
                <Td>
                  <button
                    type="button"
                    onClick={() => deleteRow(row.id)}
                    style={{ border: "1px solid #fecaca", background: "#fff1f2", color: "#be123c", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12 }}
                  >
                    Delete
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f1f5f9", fontWeight: 700, fontSize: 13 }}>
              <td style={cell} colSpan={4}>Totals</td>
              <td style={cell}>{fmtZAR(totals.tenderedRandValue)}</td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}>{fmtZAR(totals.valueOfVariance)}</td>
              <td style={cell}>{fmtZAR(totals.confirmedRandValue)}</td>
              <td style={cell} colSpan={2}></td>
              <td style={cell}>{fmtZAR(totals.total)}</td>
              <td style={cell}>{fmtZAR(totals.total)}</td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}>{fmtZAR(totals.totalClaimed)}</td>
              <td style={cell}>{fmtZAR(totals.previousClaim)}</td>
              <td style={{ ...cell, color: "#0369a1" }}>{fmtZAR(totals.thisClaim)}</td>
              <td style={cell}>{fmtZAR(totals.balance)}</td>
              <td style={cell} colSpan={6}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const cell = { borderBottom: "1px solid #e2e8f0", padding: "7px 9px", verticalAlign: "middle" };

function Th({ children, bg, title }) {
  return (
    <th title={title} style={{ ...cell, textAlign: "left", fontSize: 11, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", background: bg || "#f1f5f9", userSelect: "none" }}>
      {children}
    </th>
  );
}

function Td({ children }) {
  return <td style={cell}>{children}</td>;
}

function Calc({ children, strong, bg, color }) {
  return (
    <td style={{ ...cell, background: bg || "#f8fafc", fontWeight: strong ? 700 : 500, whiteSpace: "nowrap", color: color || "#0f172a" }}>
      {children}
    </td>
  );
}

function Input({ value, onChange, type = "text", minWidth = 110, highlight }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%", minWidth, border: `1px solid ${highlight ? "#7dd3fc" : "#cbd5e1"}`,
        borderRadius: 6, padding: "6px 8px", boxSizing: "border-box", fontSize: 13,
        background: highlight ? "#f0f9ff" : "white",
      }}
    />
  );
}

function Select({ value, options, onChange, minWidth = 130 }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", minWidth, border: "1px solid #cbd5e1", borderRadius: 6, padding: "6px 8px", background: "white", fontSize: 13 }}
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function CertCell({ value, options, onChange }) {
  if (!Array.isArray(options) || options.length === 0) {
    return <Input value={value} onChange={onChange} minWidth={110} />;
  }
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", minWidth: 110, border: "1px solid #cbd5e1", borderRadius: 6, padding: "6px 8px", background: "white", fontSize: 13 }}
    >
      <option value="">Select PC</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}